/**
 * Joins a bell feed to its engagement context (ENG-5451 / FE5).
 *
 * The two halves come from different reads on purpose. Notifications carry ids
 * and event facts; names come from the viewer-aware engagement read, so they are
 * current rather than frozen at send time and each side sees only what it is
 * entitled to. This module is where the two meet.
 *
 * A notification whose engagement is not in the context — closed and aged out of
 * the buckets, or access since revoked — still renders. It keeps its own subject
 * as the headline and loses only the name decoration, because a row that
 * silently disappeared would leave the unread count unexplainable.
 *
 * The filter's options come from the context half too, never from the rows
 * (ENG-5451 AC5). Filtering is a server-side WHERE clause, so options read off
 * the rows in hand collapsed to the single engagement being filtered to and the
 * control unmounted itself along with its way back.
 */
import { formatDate } from '../components/EngagementHub/format';
import type {
  NotificationContext,
  NotificationCopy,
  NotificationPayload,
} from './notificationCopy';
import { notificationCopy, notificationHref } from './notificationCopy';

export interface NotificationRow {
  id: string;
  code: string;
  createdAt: string;
  read: boolean;
  engagementId: string | null;
  /** Short label for the row's context line. */
  engagementLabel: string;
  copy: NotificationCopy;
  /** `/engagements/:id?tab=…`, or null when the payload named no engagement. */
  href: string | null;
}

export interface NotificationEngagementRef {
  id: string;
  label: string;
}

export interface NotificationFeedView {
  rows: NotificationRow[];
  /**
   * The filter's options — every engagement the viewer holds, read off the
   * context query and so independent of whatever the feed is currently showing.
   */
  engagements: NotificationEngagementRef[];
}

/** Shapes accepted here are the query results; kept loose so codegen types flow in. */
interface RawNotification {
  id: string;
  code: string;
  created_at: string;
  read_at?: string | null;
  payload?: (NotificationPayload & { __typename?: string }) | null;
}

interface RawEngagement {
  id: string;
  createdAt?: string | null;
  primaryProjectName?: string | null;
  expertId?: string | null;
  expertIdentity?: { name: string } | null;
}

interface FeedData {
  appNotifications?: RawNotification[] | null;
}

interface ContextData {
  myEngagementBuckets?: {
    actionNeeded?: RawEngagement[] | null;
    active?: RawEngagement[] | null;
    closed?: RawEngagement[] | null;
  } | null;
}

/** Used wherever an engagement has no name the viewer can see. */
const FALLBACK_LABEL = 'Engagement';

/** The three buckets as one list, action-needed first. Ids are unique across them. */
function flatten(data: ContextData | undefined): RawEngagement[] {
  const buckets = data?.myEngagementBuckets;
  return [...(buckets?.actionNeeded ?? []), ...(buckets?.active ?? []), ...(buckets?.closed ?? [])];
}

/** Engagement id to the context a row renders with. */
function contextIndex(all: RawEngagement[], viewerId: string) {
  const index = new Map<string, NotificationContext>();
  all.forEach((e) => {
    const label = e.primaryProjectName ?? e.expertIdentity?.name ?? FALLBACK_LABEL;
    index.set(e.id, {
      label,
      expertName: e.expertIdentity?.name ?? undefined,
      projectName: e.primaryProjectName ?? undefined,
      // Per engagement, not per session: one viewer can be the expert on one
      // and the customer on the next.
      viewerIsExpert: !!e.expertId && e.expertId === viewerId,
    });
  });
  return index;
}

/**
 * The filter's options, one per engagement the viewer holds.
 *
 * The label is the project name, so it collides whenever a project carries more
 * than one engagement — several buttons reading the same thing, filtering to
 * different feeds. Only a colliding group is suffixed; a project with one
 * engagement keeps its plain name, because a suffix on every option makes the
 * common case noisier for nothing.
 *
 * The row's own context line is left alone. It wants the project name and is
 * right to: a notification about work on Defense Tech Diligence should say so.
 * Only the option, where the name has to identify one engagement, needs more.
 */
function filterOptions(
  all: RawEngagement[],
  index: Map<string, NotificationContext>
): NotificationEngagementRef[] {
  const byLabel = new Map<string, RawEngagement[]>();
  all.forEach((e) => {
    const label = index.get(e.id)?.label ?? FALLBACK_LABEL;
    const group = byLabel.get(label);
    if (group) group.push(e);
    else byLabel.set(label, [e]);
  });

  const labels = new Map<string, string>();
  byLabel.forEach((group, label) => {
    if (group.length < 2) {
      labels.set(group[0].id, label);
      return;
    }
    const suffix = discriminator(group, index);
    group.forEach((e) => labels.set(e.id, `${label} · ${suffix(e)}`));
  });

  return all.map((e) => ({ id: e.id, label: labels.get(e.id) ?? FALLBACK_LABEL }));
}

/**
 * What tells a colliding group apart, chosen per group rather than globally.
 *
 * The expert's name is the one a reader recognises, but it is worthless on an
 * expert's own feed — they are the expert on every engagement in it — so a
 * group holding any expert-side engagement falls through to the open date, as
 * does one where two engagements share an expert. The date is on every
 * engagement, which is why it is the floor and not another candidate.
 *
 * Two engagements on one project, with one expert, opened the same day are
 * still indistinguishable. Nothing else the viewer is entitled to read
 * separates them, and inventing an id suffix would trade a rare ambiguity for a
 * permanent one.
 */
function discriminator(
  group: RawEngagement[],
  index: Map<string, NotificationContext>
): (e: RawEngagement) => string {
  const experts = group.map((e) => index.get(e.id)?.expertName);
  const expertsSeparate =
    experts.every((name) => !!name) &&
    new Set(experts).size === group.length &&
    group.every((e) => !index.get(e.id)?.viewerIsExpert);

  if (expertsSeparate) return (e) => index.get(e.id)?.expertName ?? FALLBACK_LABEL;
  return (e) => (e.createdAt ? `Opened ${formatDate(e.createdAt)}` : FALLBACK_LABEL);
}

export function buildNotificationFeed(
  feed: FeedData | undefined,
  context: ContextData | undefined,
  viewerId: string
): NotificationFeedView {
  const engagements = flatten(context);
  const index = contextIndex(engagements, viewerId);
  const notifications = feed?.appNotifications ?? [];

  const rows: NotificationRow[] = notifications.map((n) => {
    const payload = n.payload ?? {};
    const engagementId = payload.engagement_id ?? null;
    const ctx = (engagementId && index.get(engagementId)) || {
      label: FALLBACK_LABEL,
      viewerIsExpert: false,
    };

    return {
      id: n.id,
      code: n.code,
      createdAt: n.created_at,
      read: !!n.read_at,
      engagementId,
      engagementLabel: ctx.label,
      copy: notificationCopy(n.code, payload, ctx),
      href: notificationHref(payload),
    };
  });

  return { rows, engagements: filterOptions(engagements, index) };
}
